// User management module for Vuex store
import api from '@/api/api'

export default {
  namespaced: true,
  state: {
    users: [],
    loading: false,
    error: null
  },
  getters: {
    allUsers: (state) => state.users,
    loading: (state) => state.loading,
    error: (state) => state.error,

    // Filter users by role
    adminUsers: (state) => state.users.filter(user => user.role === 'admin'),
    clientUsers: (state) => state.users.filter(user => user.role === 'client'),
    candidateUsers: (state) => state.users.filter(user => user.role === 'candidate'),

    // Get user by ID
    getUserById: (state) => (id) => state.users.find(user => user._id === id || user.id === id)
  },
  mutations: {
    SET_USERS(state, users) {
      state.users = users
    },
    ADD_USER(state, user) {
      state.users.push(user)
    },
    UPDATE_USER(state, updatedUser) {
      const index = state.users.findIndex(user =>
          user._id === updatedUser._id || user.id === updatedUser.id
      )
      if (index !== -1) {
        state.users.splice(index, 1, updatedUser)
      }
    },
    REMOVE_USER(state, userId) {
      state.users = state.users.filter(user =>
          user._id !== userId && user.id !== userId
      )
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_ERROR(state) {
      state.error = null
    }
  },
  actions: {
    // Fetch all users
    async fetchUsers({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        // Using axios instead of fetch
        const response = await api.get('/users')

        commit('SET_USERS', response.data.data)
        return response.data.data
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to fetch users'
        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Create a new user
    async createUser({ commit }, userData) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        // Using axios instead of fetch
        const response = await api.post('/users', userData)

        commit('ADD_USER', response.data.data)
        return response.data.data
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to create user'
        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Update an existing user
    async updateUser({ commit }, { id, userData }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        // Using axios instead of fetch
        const response = await api.put(`/users/${id}`, userData)

        commit('UPDATE_USER', response.data.data)
        return response.data.data
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to update user'
        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Delete a user
    async deleteUser({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        // Using axios instead of fetch
        await api.delete(`/users/${id}`)

        commit('REMOVE_USER', id)
        return true
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to delete user'
        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Reset a user's password
    async resetUserPassword({ commit }, { id, password }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        // Using axios instead of fetch
        await api.put(`/users/${id}/reset-password`, { password })

        return true
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to reset password'
        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    }
  }
}
